/**
 * Classe pour calculer la durée d'animation basée sur la fréquence des mises à jour GPS
 */
export class AnimationDurationCalculator {
  // Nombre de timestamps à conserver dans la fenêtre glissante
  private readonly windowSize: number = 10;

  // Tableau des derniers timestamps de mise à jour GPS
  private timestamps: number[] = [];

  // Valeurs limites pour la durée d'animation (en ms)
  private readonly minDuration: number = 200;
  private readonly maxDuration: number = 1000;
  private readonly defaultDuration: number = 1000;

  // Facteur d'ajustement pour durée d'animation (plus grand = animation plus longue)
  private readonly durationFactor: number = 0.95; // Augmenté de 0.8 à 0.95

  // Facteur de lissage pour éviter les changements brusques
  private readonly smoothingFactor: number = 0.9; // Réduit de 0.3 à 0.2 pour un lissage plus progressif

  // Dernière durée calculée
  private lastCalculatedDuration: number = this.defaultDuration;

  // Facteur de correction pour compenser les biais potentiels
  private readonly correctionFactor: number = 1.05; // +5% comme tampon

  /**
   * Enregistre un nouveau timestamp et calcule la nouvelle durée
   */
  public updateWithNewTimestamp(timestamp: number): number {
    // Ajouter le timestamp à la liste
    this.timestamps.push(timestamp);

    // Garder uniquement les windowSize derniers timestamps
    if (this.timestamps.length > this.windowSize) {
      this.timestamps = this.timestamps.slice(-this.windowSize);
    }

    // S'il n'y a pas assez de timestamps, retourner la valeur par défaut
    if (this.timestamps.length < 3) {
      return this.defaultDuration;
    }

    // Calculer les intervalles entre les timestamps
    const intervals: number[] = [];
    for (let i = 1; i < this.timestamps.length; i++) {
      intervals.push(this.timestamps[i] - this.timestamps[i - 1]);
    }

    // Trier les intervalles et éliminer les valeurs aberrantes (10% supérieurs et inférieurs)
    const sortedIntervals = [...intervals].sort((a, b) => a - b);
    const trimmedIntervals = sortedIntervals.slice(
      Math.floor(sortedIntervals.length * 0.1),
      Math.ceil(sortedIntervals.length * 0.9)
    );

    // Calculer l'intervalle moyen sans valeurs aberrantes
    const avgInterval = trimmedIntervals.length > 0
      ? trimmedIntervals.reduce((sum, val) => sum + val, 0) / trimmedIntervals.length
      : intervals.reduce((sum, val) => sum + val, 0) / intervals.length;

    // Calculer la durée d'animation optimale avec notre facteur d'ajustement
    // et le facteur de correction pour compenser les biais
    let newDuration = Math.min(
      this.maxDuration,
      Math.max(
        this.minDuration,
        avgInterval * this.durationFactor * this.correctionFactor
      )
    );

    // Lisser la transition entre les durées pour éviter les changements brusques
    newDuration = (1 - this.smoothingFactor) * this.lastCalculatedDuration +
      this.smoothingFactor * newDuration;

    // Arrondir à l'entier le plus proche
    this.lastCalculatedDuration = Math.round(newDuration);

    // Journalisation pour le debugging
    console.log(`Intervalle moyen: ${avgInterval.toFixed(0)}ms, Durée calculée: ${this.lastCalculatedDuration}ms`);

    return this.lastCalculatedDuration;
  }

  /**
   * Récupère la dernière durée sans mettre à jour
   */
  public getLastDuration(): number {
    return this.lastCalculatedDuration;
  }

  /**
   * Définit manuellement une durée cible
   * @param targetDuration Durée cible en millisecondes
   */
  public setTargetDuration(targetDuration: number): void {
    const clampedDuration = Math.min(this.maxDuration, Math.max(this.minDuration, targetDuration));
    this.lastCalculatedDuration = clampedDuration;
  }

  /**
   * Force la durée à un pourcentage spécifique de l'intervalle moyen détecté
   * @param percentage Pourcentage de l'intervalle à utiliser comme durée (0.5 = 50%)
   */
  public forceDurationPercentage(percentage: number): void {
    if (this.timestamps.length < 3) return;

    const intervals: number[] = [];
    for (let i = 1; i < this.timestamps.length; i++) {
      intervals.push(this.timestamps[i] - this.timestamps[i - 1]);
    }

    const avgInterval = intervals.reduce((sum, val) => sum + val, 0) / intervals.length;
    const targetDuration = avgInterval * percentage;

    this.setTargetDuration(targetDuration);
  }

  /**
   * Réinitialise le calculateur
   */
  public reset(): void {
    this.timestamps = [];
    this.lastCalculatedDuration = this.defaultDuration;
  }
}
